import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Article-style markdown renderer. GFM enabled (tables, strikethrough,
 * task lists). We deliberately do NOT enable raw HTML — articles authored
 * by admins shouldn't be able to drop arbitrary <script> into the CRM, and
 * we don't have a sanitizer dep wired up. Pure markdown is enough for an
 * internal KB.
 */
interface Props {
  source: string;
  className?: string;
}

const MarkdownView: React.FC<Props> = ({ source, className }) => {
  return (
    <div className={`kb-markdown text-sm sm:text-base text-foreground ${className || ''}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, ...p }) => <h1 className="text-2xl font-bold mt-5 mb-3" {...p} />,
          h2: ({ node, ...p }) => <h2 className="text-xl font-semibold mt-4 mb-2" {...p} />,
          h3: ({ node, ...p }) => <h3 className="text-lg font-semibold mt-3 mb-2" {...p} />,
          p:  ({ node, ...p }) => <p className="mb-3 leading-relaxed whitespace-pre-wrap" {...p} />,
          ul: ({ node, ...p }) => <ul className="list-disc pl-6 mb-3 space-y-1" {...p} />,
          ol: ({ node, ...p }) => <ol className="list-decimal pl-6 mb-3 space-y-1" {...p} />,
          li: ({ node, ...p }) => <li className="leading-relaxed" {...p} />,
          // A link to an uploaded media file renders as the right player /
          // download chip based on its extension; everything else stays a
          // normal link. Lets the editor insert plain `[name](url)` for
          // video/audio/docs and have it "just work" in the article.
          a:  ({ node, href, children, ...p }: any) => {
            const url: string = href || '';
            const ext = (url.split('?')[0].split('.').pop() || '').toLowerCase();
            if (['mp4', 'webm', 'mov'].includes(ext)) {
              return <video controls src={url} className="max-w-full rounded-md my-2 max-h-[480px]" />;
            }
            if (['mp3', 'ogg', 'wav', 'm4a', 'weba'].includes(ext)) {
              return <audio controls src={url} className="w-full my-2" />;
            }
            // Any other uploaded file (doc, archive, script/.bat/.ps1/…) → a
            // download chip. The server forces attachment download for these,
            // so nothing executes in the browser.
            const isUpload = url.includes('/api/kb/media/');
            const isScript = ['bat', 'cmd', 'ps1', 'sh', 'py', 'reg', 'vbs', 'ahk',
                              'sql', 'json', 'yml', 'yaml', 'xml', 'ini', 'cfg', 'conf'].includes(ext);
            const isArchive = ['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2', 'xz', 'cab'].includes(ext);
            const isCert = ['cer', 'crt', 'der', 'pem', 'pfx', 'p12', 'p7b', 'p7c', 'crl', 'csr', 'key', 'auth', 'esl'].includes(ext);
            if (isUpload && ext) {
              return (
                <a href={url} target="_blank" rel="noreferrer" download
                   className="inline-flex items-center gap-2 my-1 px-3 py-2 rounded-md border border-border bg-muted/50 hover:bg-muted text-sm text-foreground no-underline">
                  {isCert ? '🔐' : isArchive ? '🗜️' : isScript ? '⚙️' : '📎'} {children || 'Файл'}
                  <span className="text-xs text-muted-foreground uppercase">{ext}</span>
                </a>
              );
            }
            return (
              <a href={url} className="text-primary underline hover:no-underline"
                 target="_blank" rel="noreferrer" {...p}>{children}</a>
            );
          },
          blockquote: ({ node, ...p }) => (
            <blockquote className="border-l-4 border-primary/40 pl-4 italic text-muted-foreground mb-3" {...p} />
          ),
          code: ({ node, className: cn, children, ...p }: any) => {
            const inline = !cn;
            if (inline) {
              return <code className="px-1 py-0.5 rounded bg-muted text-foreground text-[0.9em]" {...p}>{children}</code>;
            }
            return (
              <pre className="bg-muted rounded-lg p-3 mb-3 overflow-x-auto text-xs sm:text-sm">
                <code className={cn} {...p}>{children}</code>
              </pre>
            );
          },
          table: ({ node, ...p }) => (
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full border border-border text-sm" {...p} />
            </div>
          ),
          th: ({ node, ...p }) => <th className="border border-border px-3 py-2 bg-muted text-left" {...p} />,
          td: ({ node, ...p }) => <td className="border border-border px-3 py-2 align-top" {...p} />,
          hr: ({ node, ...p }) => <hr className="my-4 border-border" {...p} />,
          img: ({ node, ...p }: any) => (
            <img className="max-w-full rounded-md my-2" loading="lazy" alt={p.alt || ''} {...p} />
          ),
        }}
      >
        {source}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownView;
